import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

import PostCard from "@/components/PostCard";
import PhotoGallery from "@/components/PhotoGallery";
import ProfileBattles from "@/components/ProfileBattles";
import BambooLeaderboard from "@/components/BambooLeaderboard";
import { PROFILE } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

import VaultAndMenu from "@/pages/profile/VaultAndMenu";
import ProfileTabs from "@/pages/profile/ProfileTabs";
import ProfileHero from "@/pages/profile/ProfileHero";
import WallComposer from "@/components/WallComposer";

/**
 * Profile page shell — owns state, data fetching, avatar upload, and save.
 * Presentation lives under `pages/profile/*` so future profile tweaks
 * (new stats, extra edit sections, badges, etc.) don't reshape this file.
 */
export default function ProfilePage() {
  const { handle } = useParams();
  const { user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [composerOpen, setComposerOpen] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get(`/users/${handle}`);
      setProfile(data);
    } catch (e) {
      setProfile(false);
    }
  }, [handle]);

  useEffect(() => { window.scrollTo(0, 0); loadProfile(); }, [loadProfile]);

  const loadPosts = useCallback(() => {
    if (!profile || profile === false) return;
    api.get(`/posts`, { params: { author_id: profile.id } })
      .then(({ data }) => setPosts(data))
      .catch((e) => console.debug("[profile] posts fetch failed", e));
  }, [profile]);
  useEffect(() => { loadPosts(); }, [loadPosts]);

  if (profile === false) {
    return (
      <div className="p-6 lg:p-8">
        <div className="card-surface p-12 text-center">
          <div className="font-display text-2xl">User not found.</div>
        </div>
      </div>
    );
  }
  if (!profile) return null;

  const isMe = me && me.id === profile.id;

  return (
    <div data-testid={PROFILE.root} className="px-4 sm:px-6 lg:px-8 pt-0 sm:pt-6 pb-10 max-w-4xl mx-auto">
      <ProfileHero
        profile={profile}
        isMe={isMe}
        me={me}
        onReload={loadProfile}
        onCreatePost={() => setComposerOpen(true)}
      />

      <div className="mt-6">
        <ProfileTabs
          handle={handle}
          profile={profile}
          posts={posts}
          isMe={isMe}
          onCreatePost={() => setComposerOpen(true)}
          onPostsChanged={loadPosts}
          vault={<PhotoGallery profile={profile} onChanged={loadProfile} />}
          extraVault={isMe && me ? <VaultAndMenu me={me} isAdmin={me.role === "admin"} /> : null}
          leaderboard={<><BambooLeaderboard highlightId={profile?.id} /><ProfileBattles handle={handle} /></>}
        />
      </div>

      {isMe && <WallComposer open={composerOpen} onOpenChange={setComposerOpen} onCreated={() => loadPosts()} />}
    </div>
  );
}
